import type { Move } from '../../../schemas/move';

/**
 * Mosse avanzate del Chierico L6-10 (manuale V1, rr. 2862-2960). 14 mosse.
 *
 * Pattern notabili:
 * - **`replaces`**: *Invincibilità Divina* sostituisce *Intervento Divino*;
 *   *Martire* sostituisce *Penitenza*; *Provvidenza* sostituisce *Serenità*;
 *   *Magia Potenziata Superiore* sostituisce *Magia Potenziata*.
 * - **Cross-class restriction `prerequisites.anyOf`** (FI-19) per
 *   *Teologia Applicata* (idem alle 5 di L2-5: Divinità OR Divinità della
 *   Natura OR Ordine Sacro).
 * - **Cross-class restriction in description** (Magia Potenziata Superiore):
 *   "Se hai già preso questa mossa come Mago, non osare pretenderla anche
 *   da Colui!".
 * - **Mossa che sblocca uno spell**: *Primo Soccorso Migliorato* aggiunge
 *   *Curare Ferite Moderate* alla lista incantesimi del PG. Pattern
 *   collegato a **FI-24** (`SpellAccess.requiresMove`) — workaround
 *   description-only in questa PR.
 * - ***Apoteosi***: pattern non strutturato (genera mossa runtime).
 *   Description-only.
 * - ***Teologia Applicata***: aggiunge una seconda Tradizione Sacra.
 *   Pattern multi-tradizione non modellato in questa PR (fuori scope L1).
 */

const armaBenedetta: Move = {
  id: 'chierico.arma-benedetta',
  name: 'Arma Benedetta',
  type: 'advanced_6_10',
  classId: 'chierico',
  trigger: 'Quando crei o modifichi la tua *Arma Consacrata*',
  description:
    "Aggiungi all'elenco di *Arma Consacrata* le opzioni +2 *perforante* (al posto di +1 *perforante*), *impatto* e *anatema* (è in grado di ignorare le resistenze e le immunità, ma non l'Armatura, di qualsiasi creatura invisa alla tua divinità). Inoltre, puoi conferire due etichette alla tua arma, anziché una sola.",
  tags: [],
  prerequisites: ['chierico.arma-consacrata'],
  replaces: [],
};

const armaturaDivina: Move = {
  id: 'chierico.armatura-divina',
  name: 'Armatura Divina',
  type: 'advanced_6_10',
  classId: 'chierico',
  trigger: 'Quando subisci danni da poteri o creature invise alla tua divinità',
  description:
    'Il bonus di Armatura concesso da *Protezione Divina* contro i danni provocati da poteri o creature invise alla tua divinità passa da +2 a +3, e, inoltre, ottieni +1 Armatura.',
  tags: [],
  prerequisites: ['chierico.protezione-divina'],
  replaces: [],
};

const invincibilitaDivina: Move = {
  id: 'chierico.invincibilita-divina',
  name: 'Invincibilità Divina',
  type: 'advanced_6_10',
  classId: 'chierico',
  trigger: 'Quando ti *accampi* e preghi il tuo dio, oppure tu o un alleato subite danni',
  description:
    'Quando ti *accampi* e preghi il tuo dio, al risveglio perdi tutte le prese che ti sono rimaste e prendi 2.\n\nQuando tu o un alleato subite danni, spendi 1 presa per annullare il danno invocando la tua divinità, che interviene con una manifestazione appropriata (un soffio improvviso di vento, uno scivolone fortuito, un lampo di luce).',
  tags: [],
  prerequisites: [],
  replaces: ['chierico.intervento-divino'],
};

const primoSoccorsoMigliorato: Move = {
  id: 'chierico.primo-soccorso-migliorato',
  name: 'Primo Soccorso Migliorato',
  type: 'advanced_6_10',
  classId: 'chierico',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    'Aggiungi *Curare Ferite Moderate* alla lista di incantesimi che le tue Tradizioni Sacre ti consentono di lanciare.',
  tags: [],
  prerequisites: ['chierico.primo-soccorso'],
  // FI-24: prerequisito narrativo dell'orazione *Curare Ferite Moderate*
  // (`chierico.curare-ferite-moderate`). Enforcement runtime via
  // `SpellAccess.requiresMove` sulla spell (vedi `data/spells/chierico.ts`).
  replaces: [],
};

const benedetto: Move = {
  id: 'chierico.benedetto',
  name: 'Benedetto',
  type: 'advanced_6_10',
  classId: 'chierico',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    'Scegli un incantesimo delle tue Tradizioni Sacre, in aggiunta a quello che hai selezionato con *Prescelto*. Da ora in poi, quando lo lanci, costa 1 presa di *Mana* in meno del normale ai fini dei requisiti e delle conseguenze di un 7-9.',
  tags: [],
  prerequisites: ['chierico.prescelto'],
  replaces: [],
};

const martire: Move = {
  id: 'chierico.martire',
  name: 'Martire',
  type: 'advanced_6_10',
  classId: 'chierico',
  trigger: 'Quando subisci danno e ti immergi nel dolore',
  description:
    "Puoi decidere di subire 1d4 danni extra (con etichetta *ignora armatura*). Se lo fai, prendi +1 al prossimo tiro di *lanciare incantesimi*, e, se l'incantesimo infligge o cura danni, ne infligge o cura, rispettivamente, 2d4 in più.",
  tags: [],
  prerequisites: [],
  replaces: ['chierico.penitenza'],
};

const provvidenza: Move = {
  id: 'chierico.provvidenza',
  name: 'Provvidenza',
  type: 'advanced_6_10',
  classId: 'chierico',
  trigger: 'Quando lanci un incantesimo',
  description:
    'Ignori la penalità di -1 causata da due incantesimi *continuati* che stai mantenendo attivi.',
  tags: [],
  prerequisites: [],
  replaces: ['chierico.serenita'],
};

const teologiaApplicata: Move = {
  id: 'chierico.teologia-applicata',
  name: 'Teologia Applicata',
  type: 'advanced_6_10',
  classId: 'chierico',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    'Scegli una seconda Tradizione Sacra dalla lista della mossa *Divinità*.\n\n**NB**: il pattern multi-tradizione del PG non è modellato strutturalmente in questa PR (fuori scope L1). La seconda Tradizione vive in description; il filtraggio della spell list resta sulla Tradizione primaria del PG.',
  tags: [],
  prerequisites: [
    {
      anyOf: ['chierico.divinita', 'druido.divinita-della-natura', 'paladino.ordine-sacro'],
    },
  ],
  replaces: [],
};

const magiaPotenziataSuperiore: Move = {
  id: 'chierico.magia-potenziata-superiore',
  name: 'Magia Potenziata Superiore',
  type: 'advanced_6_10',
  classId: 'chierico',
  trigger: 'Quando tiri per *lanciare incantesimi*',
  description:
    "Con 10+, se accetti di subire una complicazione dalla lista del 7-9, scegli un potenziamento tra questi (se applicabile, e con conseguenze a discrezione del GM):\n\n- gli effetti numerici sono massimizzati, gli altri potenziati;\n- l'effetto interessa un numero doppio di bersagli o un'area più vasta.\n\nCon un 12+, puoi sceglierne uno senza dover subire complicazioni.\n\nSe hai già preso questa mossa come Mago, non osare pretenderla anche da Colui!",
  tags: [],
  prerequisites: [],
  replaces: ['chierico.magia-potenziata'],
};

const guarireLAnima: Move = {
  id: 'chierico.guarire-l-anima',
  name: "Guarire l'Anima",
  type: 'advanced_6_10',
  classId: 'chierico',
  trigger: "Quando trascorri una notte intera a pregare per l'anima di qualcuno, inclusa la tua",
  description: 'Rimuovi una delle sue Debilitazioni, ma tu non riesci a riposare.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const secondaLaurea: Move = {
  id: 'chierico.seconda-laurea',
  name: 'Seconda Laurea',
  type: 'advanced_6_10',
  classId: 'chierico',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    'Scegli una mossa del Mago o Monaco (in base al tipo di *Seminario Sperimentale* che hai frequentato). Non scegliere mosse multiclasse.',
  tags: ['multiclasse'],
  prerequisites: ['chierico.seminario-sperimentale'],
  replaces: [],
  // FI-44 (2026-05-15, manuale V1 r. 2930): pool dipende dalla choice fatta
  // in `chierico.seminario-sperimentale`. Le 3 Regole → Monaco; le 2 mosse
  // Mago → Mago. Pool base vuoto (`from-extensions-only`).
  multiclass: {
    picks: { kind: 'from-extensions-only' },
    extendsByMoveChoice: [
      {
        moveId: 'chierico.seminario-sperimentale',
        classesByChoice: {
          'regola-battaglia': ['monaco'],
          'regola-lavoro': ['monaco'],
          'regola-pieta': ['monaco'],
          'scuola-giovani-maghi': ['mago'],
          'scudo-magico': ['mago'],
        },
      },
    ],
  },
};

const leVieDelSignoreSonoInfinite: Move = {
  id: 'chierico.le-vie-del-signore-sono-infinite',
  name: 'Le Vie del Signore sono Infinite',
  type: 'advanced_6_10',
  classId: 'chierico',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    "Scegli una mossa non multiclasse da un'altra classe. Ai fini della scelta, considera il tuo livello come fosse inferiore di uno, a meno che quella mossa non appartenga al Mago o al Monaco.",
  tags: ['multiclasse'],
  prerequisites: [],
  replaces: [],
  // FI-17 (D7): pool aperto + cap livello -1, eccezione Mago/Monaco
  // (le 2 classi sorgente del Seminario Sperimentale, manuale).
  multiclass: {
    picks: { kind: 'any-other-class' },
    levelAdjustment: {
      delta: -1,
      exceptions: { sourceClassIds: ['mago', 'monaco'] },
    },
  },
};

const apoteosi: Move = {
  id: 'chierico.apoteosi',
  name: 'Apoteosi',
  type: 'advanced_6_10',
  classId: 'chierico',
  trigger:
    'Una sola volta, dopo aver preso questa mossa, quando trascorri del tempo in preghiera in modo appropriato',
  description:
    'Vieni benedetto dalla tua divinità: descrivi come. In base alla benedizione che hai ricevuto, scegli se concordare una nuova mossa con il GM, oppure aumentare di 1 il punteggio di una Caratteristica (al massimo 18). La mossa avrà sempre l\'innesco "*Quando…*" e un effetto "*allora...*", ma non dovrà richiedere alcun tiro per essere usata.\n\n**NB**: il pattern "mossa generata a runtime dal GM" non è modellabile in dati strutturati. La mossa concordata vive nelle note del PG (gestite dal giocatore).',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const arcivescovo: Move = {
  id: 'chierico.arcivescovo',
  name: 'Arcivescovo',
  type: 'advanced_6_10',
  classId: 'chierico',
  trigger: "Quando tenti di esercitare la tua influenza all'interno della gerarchia ecclesiastica",
  description:
    "Tira+**CAR**. Con 7+, scegli cosa ottieni:\n\n- la cieca obbedienza dei tuoi sottoposti a un ordine ragionevole;\n- il favore di una figura chiave nella gerarchia ecclesiastica;\n- risorse o supporto significativi per raggiungere un tuo obiettivo;\n- un segreto o un'informazione tenuta nascosta dalla tua chiesa.\n\nCon 7-9, il GM introdurrà una complicazione o un costo.",
  roll: {
    stat: 'carisma',
    on10plus: { text: 'Scegli cosa ottieni dalla lista, senza complicazioni.' },
    on7to9: {
      text: 'Scegli cosa ottieni dalla lista, ma il GM introdurrà una complicazione o un costo.',
    },
  },
  tags: [],
  prerequisites: [
    {
      anyOf: [
        'chierico.divinita',
        'druido.divinita-della-natura',
        'monaco.monasteri',
        // FIX (PR Warlock 11/12): typo cross-class — il prereq alternativo è
        // *Faro di Devozione* del **Paladino**, non del Warlock (manuale r. 2947:
        // «Richiede: *Divinità*, *Divinità della Natura*, *Monasteri*, o *Faro
        // di Devozione*»). Il Paladino ha la mossa `faro-di-devozione` come
        // iniziale auto.
        'paladino.faro-di-devozione',
      ],
    },
  ],
  replaces: [],
};

export const chiericoAdvanced6to10Moves: readonly Move[] = [
  armaBenedetta,
  armaturaDivina,
  invincibilitaDivina,
  primoSoccorsoMigliorato,
  benedetto,
  martire,
  provvidenza,
  teologiaApplicata,
  magiaPotenziataSuperiore,
  guarireLAnima,
  secondaLaurea,
  leVieDelSignoreSonoInfinite,
  apoteosi,
  arcivescovo,
] as const;
